/* eslint-disable react/jsx-no-bind */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from 'src/ui/mui-ui-kit';
import { Form } from 'react-final-form';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { cleanseInput } from 'src/ui/util';
import { getAccount, fetchAccount, updateAccountPayment } from 'src/account';
import { getUser } from 'src/user';
import {
  SaveChangesToast,
  ConfirmChangesModal,
  InnerLoader,
  setBreadcrumbs,
  objectStringToBoolean,
  objectBooleanToString,
  UiToastAdapter,
} from 'src/ui';
import { toast } from 'react-toastify';
import {
  CardProcessingForm,
  BankForm,
  PricingForm,
  EquipmentForm,
  FeeBreakdown,
  TerminalsDisplay,
} from '../components';
const toBooleanValues = [
  'currentlyOfferingCardPresentRetail',
  'hasPosPartner',
  'currentlyOfferingCardNotPresentEcommerce',
  'hasShoppingCartVendor',
  'currentlyOfferingMailTelephoneOrdering',
];

const tabs = [
  {
    label: 'Bank Information',
    key: 'bank',
    component: BankForm,
  },
  {
    label: 'Card Processing Information',
    key: 'card',
    component: CardProcessingForm,
  },
  {
    label: 'Pricing',
    key: 'pricing',
    component: PricingForm,
  },
  {
    label: 'Equipment',
    key: 'equipment',
    component: EquipmentForm,
  },
];

function PaymentPage({
  user,
  account,
  fetchAccount,
  updateAccountPayment,
  isExpanded,
  setBreadcrumbs,
}) {
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({});
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const isSubmitting = useRef(false);

  useEffect(() => {
    async function load() {
      await Promise.all([fetchAccount(user.accountId)]);

      setLoading(false);
    }
    load();
    setBreadcrumbs([{ label: 'Payment', href: '/payment' }]);
  }, []);

  useEffect(() => {
    if (account.payment && Object.keys(initialValues).length < 1) {
      setInitialValues({
        ...objectBooleanToString(account.payment, toBooleanValues),
      });
    }
    if (isSubmitting.current) {
      if (account.isError()) {
        toast(
          UiToastAdapter(
            'An error occurred while updating your payment settings.',
            'danger'
          )
        );
      } else {
        toast(UiToastAdapter('Payment updated successfully!', 'success'));
      }
      isSubmitting.current = false;
    }
  }, [account]);

  const toastContainerClassName = classNames('bottom-toast-container', {
    'is-condensed': !isExpanded,
  });

  function showModal() {
    setConfirmModalVisible(true);
  }

  function hideModal() {
    setConfirmModalVisible(false);
  }

  async function onSubmit(values) {
    setLoading(true);
    isSubmitting.current = true;

    const finalValues = {
      accountId: user.accountId,
      ...objectStringToBoolean(values, toBooleanValues),
    };
    await updateAccountPayment(finalValues);
    setInitialValues(finalValues);

    setLoading(false);
  }

  return (
    <div className="payment-page ui-tabbed-intro">
      <Card className="intro">
        <CardContent>
          <h2 className="has-text-info has-text-weight-semibold is-size-5">
            Payments
          </h2>
          <p>
            In order to process payments on behalf of the bank, we’ll need to
            collect some additional information <br />
            regarding payments and the current processing methods for your
            business.
          </p>
        </CardContent>
      </Card>

      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        subscription={{
          values: {
            sameAsFeeAccount: true,
          },
          submitting: true,
          hasValidationErrors: true,
          dirty: true,
          dirtySinceLastSubmit: true,
        }}
        mutators={{
          cleanse: ([event, cleansing], state, { changeValue }) => {
            cleanseInput(event, state, changeValue, cleansing);
          },
        }}
        render={({
          handleSubmit,
          values,
          submitting,
          hasValidationErrors,
          dirty,
          dirtySinceLastSubmit,
          form,
        }) => (
          <form onSubmit={handleSubmit}>
            <InnerLoader visible={loading} />
            <div className={toastContainerClassName}>
              <SaveChangesToast
                message="Click the button to the right to save your changes"
                isLoading={submitting}
                isDisabled={submitting || hasValidationErrors}
                isVisible={dirty || dirtySinceLastSubmit}
                onClickButton={showModal}
              />
            </div>
            <ConfirmChangesModal
              title="Payment"
              isActive={confirmModalVisible}
              onClickCancel={hideModal}
              onClickSubmit={hideModal}
            />
            
            {/* Always show Bank Information (Settlement & Fee Account) */}
            <Card style={{ marginBottom: '10px' }}>
              <CardContent>
                <BankForm values={values} form={form} />
              </CardContent>
            </Card>
            
            {/* Conditionally show sections based on POS payments enabled */}
            {account.enablePosPayments ? (
              <>
                {/* POS Payment Sections */}
                <Card style={{ marginBottom: '10px' }}>
                  <CardContent>
                    <FeeBreakdown account={account} user={user} />
                  </CardContent>
                </Card>
                
                <Card style={{ marginBottom: '10px' }}>
                  <CardContent>
                    <TerminalsDisplay account={account} user={user} />
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Legacy Payment Processing Sections */}
                {tabs.slice(1).map(({ key, component: TabComponent }) => {
                  return (
                    <Card key={key} style={{ marginBottom: '10px' }}>
                      <CardContent>
                        <TabComponent values={values} form={form} />
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </form>
        )}
      />
    </div>
  );
}

PaymentPage.propTypes = {
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  fetchAccount: propTypes.func.isRequired,
  updateAccountPayment: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  isExpanded: propTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
  };
}

const connectedPaymentPage = connect(mapStateToProps, {
  fetchAccount,
  updateAccountPayment,
  setBreadcrumbs,
})(PaymentPage);

export { connectedPaymentPage as PaymentPage };
